// A method screen's edits: what staff can change (state, label, notice,
// staff note, the kind's options), compared with the method as auth last
// answered it. Only what changed is sent (PUT /signin-method/:kind leaves a
// key it isn't sent alone).
import { MAX_NOTICE, MAX_STAFF_NOTE, labelProblem, lengthProblem } from './signin-method-words'

// The editable part of a detail MethodView. A kind not set up reads as off,
// with its default label.
export const toDraft = method => ({
  state: (method && method.state) || 'off',
  label: (method && (method.label || method.defaultLabel)) || '',
  notice: (method && method.notice) || '',
  staffNote: (method && method.staffNote) || '',
  settings: Object.fromEntries(((method && method.options) || []).map(o => [o.key, Boolean(o.value === undefined || o.value === null ? o.default : o.value)]))
})

const trimmed = text => String(text || '').trim()

// What differs, by name: state, label, notice, staffNote, and settings.<key>
// for each option.
export const changesOf = (draft, base) => {
  if (!draft || !base) return []
  const changes = []
  if (draft.state !== base.state) changes.push('state')
  if (trimmed(draft.label) !== trimmed(base.label)) changes.push('label')
  if (trimmed(draft.notice) !== trimmed(base.notice)) changes.push('notice')
  if (trimmed(draft.staffNote) !== trimmed(base.staffNote)) changes.push('staffNote')
  Object.keys(draft.settings || {}).forEach(key => {
    if (Boolean(draft.settings[key]) !== Boolean((base.settings || {})[key])) changes.push(`settings.${key}`)
  })
  return changes
}

// The PUT body: only the changed keys ("" clears a notice or note), and
// confirmEntryPoint when staff chose "Switch off anyway" for a kind whose
// button must stay visible (auth refuses state off without it: 409
// signin_method.entry_point_required).
export const bodyOf = (draft, base, { confirmEntryPoint = false } = {}) => {
  const body = {}
  const changes = changesOf(draft, base)
  if (changes.includes('state')) body.state = draft.state
  if (changes.includes('label')) body.label = trimmed(draft.label)
  if (changes.includes('notice')) body.notice = trimmed(draft.notice)
  if (changes.includes('staffNote')) body.staffNote = trimmed(draft.staffNote)
  const settings = {}
  changes.filter(c => c.startsWith('settings.')).forEach(c => {
    const key = c.slice('settings.'.length)
    settings[key] = Boolean(draft.settings[key])
  })
  if (Object.keys(settings).length) body.settings = settings
  if (body.state === 'off' && confirmEntryPoint) body.confirmEntryPoint = true
  return body
}

// The edits auth would refuse, by field. The label is judged only once it
// is changed: a label saved before the rule stays until someone edits it.
export const problemsOf = (draft, base, defaultLabel) => {
  const problems = {}
  if (!draft) return problems
  if (trimmed(draft.label) !== trimmed(base && base.label)) {
    const label = labelProblem(draft.label, defaultLabel)
    if (label) problems.label = label
  }
  const notice = lengthProblem(draft.notice, MAX_NOTICE, 'notice')
  if (notice) problems.notice = notice
  const note = lengthProblem(draft.staffNote, MAX_STAFF_NOTE, 'staff note')
  if (note) problems.staffNote = note
  return problems
}
